import React, { ComponentType } from 'react'
import Select from 'react-select'
import { GenericCheckbox } from '../../../../../components/Inputs'
import NullSafeNumericInput from '../../../../../components/NullSafeNumericInput'
import { ProductFormGroup } from '../ProductFormGroup'
import { InputProps } from '../ChannelShared/SharedInputs'
import { renderError } from '../Helpers'

export const whoMade = {
  i_did: 'I did',
  collective: 'A collective',
  someone_else: 'Someone else'
}

export const whenMade = {
  made_to_order: 'Made to order',
  '2020_2025': '2020 - 2025',
  '2010_2019': '2010 - 2019',
  '2006_2009': '2006 - 2009',
  before_2006: 'Before 2006',
  '2000_2005': '2000 - 2005',
  '1990s': '1990s',
  '1980s': '1980s',
  '1970s': '1970s',
  '1960s': '1960s',
  '1950s': '1950s',
  '1940s': '1940s',
  '1920s': '1930s',
  '1930s': '1920s',
  '1910s': '1910s',
  '1900s': '1900s',
  '1800s': '1800s',
  '1700s': '1700s',
  before_1700: 'Before 1700'
}

export const etsyType = {
  physical: 'Physical',
  download: 'Download',
  both: 'Both'
}

export const listingDuration = {
  '1': '1 Day',
  '3': '3 Days',
  '5': '5 Days',
  '7': '7 Days',
  '10': '10 Days',
  '30': '30 Days',
  '60': '60 Days',
  '90': '90 Days',
  '120': '120 Days'
}

function swapKeysAndValues(obj: Record<string, string>): Record<string, string> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    acc[value] = key
    return acc
  }, {})
}

type EtsyField = {
  title: string
  transform?: (value: any) => any
  type: 'string' | 'number' | 'boolean' | 'options' | 'currency'
  errorKey: string // The key on the Listing to look for when errors are returned from the Lister API
  values?: string[]
  default?: any
  showRequiredLabel: boolean
}

export const etsyFields: Record<string, EtsyField> = {
  who_made: {
    type: 'options',
    values: Object.values(whoMade),
    title: 'Who made it?',
    showRequiredLabel: true,
    errorKey: 'whoMade',
    default: Object.keys(whoMade).find(k => k === 'someone_else'),
    transform: value => swapKeysAndValues(whoMade)[value]
  },
  when_made: {
    type: 'options',
    values: Object.values(whenMade),
    title: 'When was it made?',
    showRequiredLabel: true,
    errorKey: 'whenMade',
    transform: value => swapKeysAndValues(whenMade)[value]
  },
  // TODO: TT-878
  // materials: {
  //   type: 'string',
  //   title: 'Materials (comma separated)',
  //   showRequiredLabel: false,
  //   errorKey: 'materials',
  //   transform: string => string.split(',').map(s => s.trim())
  // },
  // processing_min: {
  //   type: 'number',
  //   title: 'Processing Min',
  //   showRequiredLabel: false,
  //   errorKey: 'processingMin'
  // },
  // processing_max: {
  //   type: 'number',
  //   title: 'Processing Max',
  //   showRequiredLabel: false,
  //   errorKey: 'processingMax'
  // },
  // tags: {
  //   type: 'string',
  //   title: 'Tags (comma separated)',
  //   transform: string => string.split(',').map(s => s.trim()),
  //   showRequiredLabel: false,
  //   errorKey: 'tags'
  // },
  // styles: {
  //   type: 'string',
  //   title: 'Styles (comma separated)',
  //   transform: string => string.split(',').map(s => s.trim()),
  //   showRequiredLabel: false
  //   errorKey: 'styles',
  // },
  // personalization_instructions: {
  //   type: 'string',
  //   title: 'Personalization Instructions',
  //   showRequiredLabel: false,
  //   errorKey: 'personalizationInstructions'
  // },
  item_type: {
    type: 'options',
    values: Object.values(etsyType),
    title: 'Type',
    showRequiredLabel: true,
    errorKey: 'itemType',
    default: Object.keys(etsyType).find(k => k === 'physical'),
    transform: value => swapKeysAndValues(etsyType)[value]
  },
  is_supply: {
    type: 'boolean',
    title: 'Is Supply',
    showRequiredLabel: false,
    errorKey: 'isSupply'
  },
  // is_customizable: {
  //   type: 'boolean',
  //   title: 'Is Customizable',
  //   showRequiredLabel: false,
  //   errorKey: 'isCustomizable'
  // },
  is_taxable: {
    type: 'boolean',
    title: 'Is Taxable',
    showRequiredLabel: false,
    errorKey: 'isTaxable'
  }
  // is_personalizable: {
  //   type: 'boolean',
  //   title: 'Is Personalizable',
  //   showRequiredLabel: false
  // },
  // personalization_is_required: {
  //   type: 'boolean',
  //   title: 'Personalization Is Required',
  //   showRequiredLabel: false,
  //   errorKey: 'personalizationIsRequired'
  // }
}

interface EtsyFieldComponentProps extends InputProps {
  field: EtsyField
  value: any
}

function BooleanComponent(props: EtsyFieldComponentProps) {
  return (
    <ProductFormGroup
      title={props.field.title}
      error={props.error}
      showRequiredLabel={props.field.showRequiredLabel}
      helperText={renderError(props.error)}
    >
      <GenericCheckbox
        value={props.value}
        onChange={value => props.onListingChange(props.field.transform ? props.field.transform(value) : value)}
      />
    </ProductFormGroup>
  )
}

function NumericComponent(props: EtsyFieldComponentProps) {
  /* TODO: check that the onListingChange substitute works well everywhere in this file */
  return (
    <ProductFormGroup
      title={props.field.title}
      error={props.error}
      showRequiredLabel={props.field.showRequiredLabel}
      helperText={renderError(props.error)}
    >
      <NullSafeNumericInput
        required={props.showRequiredLabel} // TODO: check if this is correct
        min={0}
        minorStepSize={1}
        clampValueOnBlur
        selectAllOnFocus
        onValueChange={(num, _s) => props.onListingChange(props.field.transform ? props.field.transform(num) : num)}
        value={props.value}
      />
    </ProductFormGroup>
  )
}

function DropdownComponent(props: EtsyFieldComponentProps) {
  // since we gotta send a value, not the key, it's hard to map the value back to the key
  // hence, gotta extract this little helper with all the dropdowns combined
  const possibleDropdownValues = {
    ...whoMade,
    ...whenMade,
    ...etsyType,
    ...listingDuration
  }

  return (
    <ProductFormGroup
      title={props.field.title}
      error={props.error}
      showRequiredLabel={props.field.showRequiredLabel}
      helperText={renderError(props.error)}
    >
      <Select
        required={props.field.showRequiredLabel}
        value={possibleDropdownValues[props.value]}
        onChange={v => props.onListingChange(props.field.transform ? props.field.transform(v) : v)}
        labelKey="name"
        valueKey="value"
        clearable={false}
        classNamePrefix="form-component react-select-same-height-as-blueprint"
        simpleValue
        options={props.field.values.map(value => ({ name: value, value }))}
      />
    </ProductFormGroup>
  )
}

function StringComponent(props: EtsyFieldComponentProps) {
  return (
    <ProductFormGroup
      title={props.field.title}
      error={props.error}
      showRequiredLabel={props.field.showRequiredLabel}
      helperText={renderError(props.error)}
    >
      <input
        required={props.field.showRequiredLabel}
        className="bp3-input form-component" // TODO: check this component, maybe it should be a BP component?
        defaultValue={props.value}
        onChange={e =>
          props.onListingChange(props.field.transform ? props.field.transform(e.target.value) : e.target.value)
        }
      />
    </ProductFormGroup>
  )
}

function CurrencyComponent(props: EtsyFieldComponentProps) {
  return (
    <ProductFormGroup
      title={props.field.title}
      error={props.error}
      showRequiredLabel={props.field.showRequiredLabel}
      helperText={renderError(props.error)}
    >
      <NullSafeNumericInput
        className="form-component"
        min={0}
        minorStepSize={0.01}
        clampValueOnBlur
        selectAllOnFocus
        onValueChange={(num, _s) => props.onListingChange(props.field.transform ? props.field.transform(num) : num)}
        defaultValue={props.value}
      />
    </ProductFormGroup>
  )
}

export const lookupComponent: Record<EtsyField['type'], ComponentType<EtsyFieldComponentProps>> = {
  boolean: BooleanComponent,
  number: NumericComponent,
  options: DropdownComponent,
  string: StringComponent,
  currency: CurrencyComponent
}
